import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface LogLine {
  key: string | number;
  ts?: string;
  level?: 'debug' | 'info' | 'warn' | 'error';
  /** Stage (job logs) or service (runtime logs). */
  source?: string;
  message: string;
}

const LINE_CLASS: Record<string, string> = {
  error: 'bg-error/10 dr-ink-error',
  warn: 'bg-warning/15',
  debug: 'text-base-content/55',
  info: '',
};

const LEVEL_BADGE: Record<string, string> = {
  error: 'badge badge-soft badge-error badge-xs',
  warn: 'badge badge-soft badge-warning badge-xs',
  debug: 'badge badge-ghost badge-xs',
};

function time(ts?: string): string {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleTimeString(undefined, { hour12: false });
}

/** Monospace log output with level colouring and an auto-scroll toggle. */
export function LogViewer({ lines, emptyText = 'No log output yet.', toolbar, busy }: { lines: LogLine[]; emptyText?: ReactNode; toolbar?: ReactNode; busy?: boolean }) {
  const [autoscroll, setAutoscroll] = useState(true);
  const [wrap, setWrap] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    const el = ref.current;
    if (autoscroll && el) el.scrollTop = el.scrollHeight;
  }, [lines, autoscroll]);

  return (
    <div className="dr-surface overflow-hidden rounded-box">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-hairline px-3 py-2 text-sm">
        {toolbar}
        <label className="flex cursor-pointer items-center gap-2 text-base-content/70" htmlFor={`${id}-auto`}>
          <input id={`${id}-auto`} type="checkbox" className="toggle toggle-xs" checked={autoscroll} onChange={(e) => setAutoscroll(e.target.checked)} />
          Auto-scroll
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-base-content/70" htmlFor={`${id}-wrap`}>
          <input id={`${id}-wrap`} type="checkbox" className="toggle toggle-xs" checked={wrap} onChange={(e) => setWrap(e.target.checked)} />
          Wrap lines
        </label>
        <span className="ml-auto flex items-center gap-2 text-xs text-base-content/55 tabular-nums">
          {busy ? <span className="loading loading-dots loading-xs" aria-label="Receiving" /> : null}
          {lines.length} lines
        </span>
      </div>
      <div
        ref={ref}
        role="log"
        aria-label="Log output"
        tabIndex={0}
        className="max-h-[60vh] min-h-48 overflow-auto bg-base-200/60 p-3 font-mono text-xs leading-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset"
      >
        {lines.length === 0 ? (
          <p className="font-sans text-sm text-base-content/60">{emptyText}</p>
        ) : (
          lines.map((l) => (
            <div key={l.key} className={`rounded-sm px-1 ${LINE_CLASS[l.level ?? 'info']} ${wrap ? 'break-anywhere whitespace-pre-wrap' : 'whitespace-pre'}`}>
              {l.ts ? <span className="mr-2 text-base-content/45 select-none">{time(l.ts)}</span> : null}
              {l.source ? <span className="dr-ink-secondary mr-2">{l.source}</span> : null}
              {l.level && l.level !== 'info' ? <span className={`${LEVEL_BADGE[l.level]} mr-2 align-middle uppercase`}>{l.level}</span> : null}
              {l.message}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
